import {
  CancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
  isCancelledError,
  notifyManager,
} from "@tanstack/react-query";
import {
  createRouteMask,
  createRouter as createTanStackRouter,
  isRedirect,
} from "@tanstack/react-router";
import { routerWithQueryClient } from "@tanstack/react-router-with-query";
import { configResponsive } from "ahooks";
import { isEqual } from "radashi";
import toast from "react-hot-toast";
import { DefaultCatchBoundary } from "./components/default-catch-boundary";
import { NotFound } from "./components/not-found";
import { logger } from "./libs/logger";
import { routeTree } from "./routeTree.gen";

export function createRouter() {
  if (typeof document !== "undefined") {
    notifyManager.setScheduler(window.requestAnimationFrame);
  }

  const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
      },
    },
    mutationCache: new MutationCache({
      onError: (e) => handleGlobalError(e, "Failed to perform mutation"),
      onMutate: (variables) => {
        // ensure that there is only one mutation of the same kind running at a time
        const currentMutationCount = queryClient.isMutating({
          predicate: (mutation) => isEqual(mutation.state.variables, variables),
          exact: true,
          fetching: true,
        });
        if (currentMutationCount > 1) {
          return Promise.reject(new CancelledError({ silent: true }));
        }
      },
    }),
    queryCache: new QueryCache({
      onError: (e) => handleGlobalError(e, "Failed to fetch query"),
    }),
  });

  const router = routerWithQueryClient(
    createTanStackRouter({
      routeTree,
      defaultPreload: "intent",
      defaultErrorComponent: DefaultCatchBoundary,
      defaultNotFoundComponent: () => <NotFound />,
      context: {
        queryClient,
        auth: { session: null, user: null },
        features: [],
      },
      search: { strict: true },
      defaultViewTransition: true,
      routeMasks: [
        createRouteMask({
          routeTree,
          from: "/artwork/$artworkId/modal",
          to: "/artwork/$artworkId",
          params: true,
        }),
      ],
    }),
    queryClient,
  );

  function handleGlobalError(error: Error, logMessage: string) {
    if (isCancelledError(error) && error.silent) {
      return;
    }

    if (isRedirect(error)) {
      router.navigate(
        router.resolveRedirect({
          ...error,
          _fromLocation: router.state.location,
        }),
      );
    }

    logger.error(error, logMessage);

    if (!import.meta.env.SSR) {
      toast.error(error.message);
    }
  }

  configResponsive({
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
  });

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
